import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { workKindLabel } from "@/lib/workKind";
import {
  countSelected,
  initialCheckedIds,
  reasonLabel,
  toggleHandoffSelection,
} from "@/lib/handoffBatch";

import { API_BASE_URL } from "@/lib/apiBase";

function CandidateRow({ node, depth, checked, onToggle, disabled }) {
  const isRoot = depth === 0;
  const isChecked = checked.has(node.post_id);
  const reason = reasonLabel(node.reason);
  const locked = isRoot || !node.eligible || disabled;

  return (
    <li className="space-y-1">
      <label
        className={`flex items-start gap-2 text-sm ${
          locked ? "opacity-70" : "cursor-pointer"
        }`}
        style={{ paddingLeft: `${depth}rem` }}
      >
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 rounded border border-input"
          checked={isChecked}
          disabled={locked}
          onChange={(e) => onToggle(node.post_id, e.target.checked)}
        />
        <span className="min-w-0">
          <span className="font-medium">
            {workKindLabel(node.work_kind)} · {node.title || "Untitled"}
          </span>
          {reason ? (
            <span className="block text-xs text-muted-foreground">{reason}</span>
          ) : null}
        </span>
      </label>
      {node.children?.length > 0 ? (
        <ul className="space-y-1">
          {node.children.map((child) => (
            <CandidateRow
              key={child.post_id}
              node={child}
              depth={depth + 1}
              checked={checked}
              onToggle={onToggle}
              disabled={disabled}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

const HandoffDialog = ({
  open,
  onClose,
  post,
  orgId,
  currentTeamId,
  accessToken,
  onSuccess,
}) => {
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamSearch, setTeamSearch] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [batchApiAvailable, setBatchApiAvailable] = useState(false);
  const [checked, setChecked] = useState(() => new Set());

  const postId = post?.post_id || post?.postId;
  const tree = candidates?.tree || null;
  const hasBatchUi = Boolean(batchApiAvailable && tree?.children?.length);
  const rootBlocked = Boolean(tree && tree.eligible === false);
  const rootBlockReason = reasonLabel(tree?.reason);

  useEffect(() => {
    if (!open) {
      setTeamSearch("");
      setSelectedTeamId("");
      setNote("");
      setError(null);
      setCandidates(null);
      setBatchApiAvailable(false);
      setChecked(new Set());
      return;
    }

    if (!accessToken || !orgId) return;

    const loadTeams = async () => {
      setLoadingTeams(true);
      setError(null);
      try {
        const [organizationResponse, requestableResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/team/organization`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: orgId },
          }),
          axios.get(`${API_BASE_URL}/team/requestable`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: orgId },
          }),
        ]);
        const merged = [
          ...(Array.isArray(organizationResponse.data)
            ? organizationResponse.data
            : []),
          ...(Array.isArray(requestableResponse.data)
            ? requestableResponse.data
            : []),
        ];
        const list = Array.from(
          new Map(
            merged
              .map((t) => {
                const id = t.team_id ?? t.teamId;
                return id != null ? [id, t] : null;
              })
              .filter(Boolean)
          ).values()
        );
        const currentId = Number(currentTeamId);
        setTeams(
          list.filter((t) => Number(t.team_id ?? t.teamId) !== currentId)
        );
      } catch (err) {
        console.error("Failed to load handoff destination teams:", err);
        setError(err.response?.data?.message || "Failed to load teams");
        setTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };

    const loadCandidates = async () => {
      if (!postId) return;
      setLoadingCandidates(true);
      setBatchApiAvailable(false);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/posts/${postId}/handoffs/candidates`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: orgId },
          }
        );
        setCandidates(response.data);
        setChecked(initialCheckedIds(response.data?.tree));
        setBatchApiAvailable(true);
      } catch (err) {
        // Pre-migration / older API: fall back to single-card handoff quietly.
        const status = err.response?.status;
        if (status !== 404 && status !== 501) {
          console.warn("Handoff candidates unavailable; using single-card mode", err);
        }
        setCandidates(null);
        setBatchApiAvailable(false);
        setChecked(new Set([Number(postId)]));
      } finally {
        setLoadingCandidates(false);
      }
    };

    loadTeams();
    loadCandidates();
  }, [open, accessToken, orgId, currentTeamId, postId]);

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) => {
      const name = (t.name || "").toLowerCase();
      const id = String(t.team_id ?? t.teamId ?? "");
      return name.includes(q) || id.includes(q);
    });
  }, [teams, teamSearch]);

  const selectedCount = countSelected(checked);
  const totalInTree = useMemo(() => {
    if (!tree) return 1;
    let n = 0;
    const walk = (node) => {
      n += 1;
      for (const c of node.children || []) walk(c);
    };
    walk(tree);
    return n;
  }, [tree]);

  const handleToggle = (id, next) => {
    if (!tree) return;
    setChecked((prev) => toggleHandoffSelection(tree, prev, id, next));
  };

  const handleSubmit = async () => {
    if (!postId) {
      setError("No card selected");
      return;
    }
    if (!selectedTeamId) {
      setError("Select a destination team");
      return;
    }
    const postIds = hasBatchUi ? [...checked] : [Number(postId)];
    if (!postIds.includes(Number(postId))) {
      postIds.push(Number(postId));
    }
    if (postIds.length < 1) {
      setError("Select at least one card to hand off");
      return;
    }
    if (rootBlocked) {
      setError(
        tree?.reason === "done"
          ? `"${post?.title || "This card"}" is in the completed work lane, so it can’t be handed off. Move it to an open lane first.`
          : `"${post?.title || "This card"}" can’t be handed off right now.`
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body = {
        to_team_id: Number(selectedTeamId),
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      // Only send post_ids when batch API is present (older APIs reject unknown fields).
      if (batchApiAvailable) {
        body.post_ids = postIds;
      }
      await axios.post(`${API_BASE_URL}/posts/${postId}/handoffs`, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { org_id: orgId },
      });
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to create handoff:", err);
      const apiMsg = err.response?.data?.message;
      setError(
        (Array.isArray(apiMsg) ? apiMsg.join(" ") : apiMsg) ||
          err.message ||
          "Failed to hand off card"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto z-[70]" overlayClassName="z-[70]">
        <DialogHeader>
          <DialogTitle>
            {hasBatchUi ? "Hand off work" : "Hand off card"}
          </DialogTitle>
          <DialogDescription>
            {hasBatchUi
              ? "Choose which linked cards move with this handoff. Selected cards freeze until the destination team accepts or rejects."
              : "Send this card to another searchable team in the organization. The card will freeze until they accept or reject."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {post?.title && (
            <p className="text-sm text-muted-foreground">
              Card:{" "}
              <span className="text-foreground font-medium">{post.title}</span>
            </p>
          )}

          {rootBlocked && (
            <Alert>
              <AlertDescription>
                {tree?.reason === "done"
                  ? `This card is in the completed work lane (${rootBlockReason || "Completed"}). Move it to an open lane before handing it off.`
                  : `This card can’t be handed off right now${
                      rootBlockReason ? ` (${rootBlockReason})` : ""
                    }.`}
              </AlertDescription>
            </Alert>
          )}

          {hasBatchUi && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Items to hand off</Label>
                <span className="text-xs text-muted-foreground">
                  {selectedCount} of {totalInTree} selected
                </span>
              </div>
              {loadingCandidates ? (
                <p className="text-sm text-muted-foreground">Loading linked work…</p>
              ) : (
                <ul className="max-h-48 overflow-y-auto rounded-md border border-border p-2 space-y-1">
                  <CandidateRow
                    node={tree}
                    depth={0}
                    checked={checked}
                    onToggle={handleToggle}
                    disabled={submitting}
                  />
                </ul>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="handoff-team-search">Destination team</Label>
            {selectedTeamId ? (
              <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
                <span className="text-sm font-medium truncate">
                  {teams.find(
                    (t) => String(t.team_id ?? t.teamId) === String(selectedTeamId)
                  )?.name || `Team ${selectedTeamId}`}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0"
                  disabled={submitting}
                  onClick={() => {
                    setSelectedTeamId("");
                    setTeamSearch("");
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <>
                <Input
                  id="handoff-team-search"
                  className="mt-1.5"
                  placeholder="Search teams…"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  disabled={loadingTeams || submitting}
                  autoComplete="off"
                />
                {loadingTeams ? (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Loading teams…
                  </p>
                ) : filteredTeams.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {teamSearch.trim()
                      ? `No teams match “${teamSearch.trim()}”.`
                      : "No other searchable teams found."}
                  </p>
                ) : (
                  <ul
                    className="mt-1.5 max-h-40 overflow-y-auto rounded-md border border-border divide-y divide-border"
                    role="listbox"
                    aria-label="Matching teams"
                  >
                    {filteredTeams.map((t) => {
                      const id = t.team_id ?? t.teamId;
                      return (
                        <li key={id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={String(id) === String(selectedTeamId)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
                            onClick={() => {
                              setSelectedTeamId(String(id));
                              setTeamSearch(t.name || "");
                            }}
                            disabled={submitting}
                          >
                            {t.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>

          <div>
            <Label htmlFor="handoff-note">Note (optional)</Label>
            <textarea
              id="handoff-note"
              className="mt-1.5 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Context for the receiving team…"
              disabled={submitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !selectedTeamId ||
              loadingCandidates ||
              rootBlocked ||
              (hasBatchUi && selectedCount < 1)
            }
          >
            {submitting
              ? "Sending…"
              : hasBatchUi
                ? `Hand off ${selectedCount} item${selectedCount === 1 ? "" : "s"}`
                : "Hand off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HandoffDialog;
